import { Component } from '@angular/core';
import Swal from 'sweetalert2';
import { WalletLookupService } from '../../services/wallet-lookup.service';
import {
  BusquedaPorUsuarioResponse,
  BusquedaPorWalletResponse,
  OrigenWallet,
  RegistroWallet,
} from '../../interfaces/wallet-lookup.interface';

/** Las dos direcciones de consulta de la pantalla. */
type Modo = 'wallet' | 'usuario';

/**
 * "Responsable por Wallet": de quién es una dirección cripto, y qué
 * direcciones tiene un cliente.
 *
 * Dual a propósito: son la misma pregunta mirada desde los dos lados, y el
 * operador que investiga un caso salta de una a la otra todo el tiempo. Por
 * eso el resultado de un modo linkea al otro (click en el usuario → sus
 * wallets; click en una wallet → sus usuarios).
 *
 * No responde por el RIESGO de la dirección: eso es "Validar Wallet"
 * (Chainalysis) y sigue viviendo aparte. Acá sólo se responde de quién es.
 *
 * Sin acciones de mutación: es una pantalla de consulta.
 */
@Component({
  selector: 'app-responsable-wallet',
  templateUrl: './responsable-wallet.component.html',
  styleUrls: ['./responsable-wallet.component.sass'],
})
export class ResponsableWalletComponent {
  modo: Modo = 'wallet';

  /** Lo que el operador pega: una dirección o un id de cliente. */
  termino = '';

  loading = false;
  buscado = false;

  resultadoWallet: BusquedaPorWalletResponse | null = null;
  resultadoUsuario: BusquedaPorUsuarioResponse | null = null;

  constructor(private service: WalletLookupService) {}

  setModo(modo: Modo): void {
    if (this.modo === modo) return;
    this.modo = modo;
    this.termino = '';
    this.limpiarResultados();
  }

  async buscar(): Promise<void> {
    const termino = this.termino.trim();
    if (!termino) {
      Swal.fire(
        'Falta el criterio',
        this.modo === 'wallet'
          ? 'Pega una dirección de wallet para buscar.'
          : 'Ingresa el ID del cliente.',
        'info',
      );
      return;
    }

    if (this.modo === 'usuario' && !/^\d+$/.test(termino)) {
      Swal.fire(
        'ID inválido',
        'El ID del cliente es un número. Si lo que tienes es una wallet, cambia de modo.',
        'info',
      );
      return;
    }

    this.loading = true;
    this.buscado = true;
    this.limpiarResultados();

    try {
      if (this.modo === 'wallet') {
        this.resultadoWallet = await this.service.buscarPorWallet(termino);
      } else {
        this.resultadoUsuario = await this.service.buscarPorUsuario(
          Number(termino),
        );
      }
    } catch (err) {
      this.mostrarError(err);
    } finally {
      this.loading = false;
    }
  }

  /** Salta de un usuario encontrado a la lista completa de sus wallets. */
  verWalletsDe(usuarioId: number): void {
    this.modo = 'usuario';
    this.termino = String(usuarioId);
    this.buscar();
  }

  /** Salta de una wallet del cliente a todos los usuarios que la tienen. */
  verUsuariosDe(address: string): void {
    this.modo = 'wallet';
    this.termino = address;
    this.buscar();
  }

  limpiar(): void {
    this.termino = '';
    this.buscado = false;
    this.limpiarResultados();
  }

  async copiar(texto: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(texto);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Copiado',
        showConfirmButton: false,
        timer: 1200,
      });
    } catch {
      // El portapapeles puede estar bloqueado por permisos del navegador. No
      // vale romper la pantalla por no poder copiar: la dirección está a la
      // vista y se puede seleccionar a mano.
    }
  }

  // ─── Presentación ───────────────────────────────────────────────────

  /**
   * Alerta sólo cuando hay MÁS DE UN cliente con la misma dirección de
   * destino: una wallet compartida es lo que el operador vino a detectar.
   * Con las de depósito no aplica —son nuestras y tienen un único dueño—.
   */
  get walletCompartida(): boolean {
    return (this.resultadoWallet?.totalUsuarios ?? 0) > 1;
  }

  etiquetaOrigen(origen: OrigenWallet): string {
    return origen === 'beneficiario'
      ? 'Beneficiario del cliente'
      : 'Depósito Binance (nuestra)';
  }

  iconoOrigen(origen: OrigenWallet): string {
    return origen === 'beneficiario' ? 'call_made' : 'call_received';
  }

  claseEstado(activa: boolean): string {
    return activa
      ? 'bg-green-50 text-green-700 border-green-200'
      : 'bg-gray-100 text-gray-600 border-gray-200';
  }

  nombreCompleto(nombre: string, apellidos: string): string {
    return `${nombre || ''} ${apellidos || ''}`.trim() || '—';
  }

  /** Las activas primero; ese es el orden en que se revisa. */
  ordenarRegistros(registros: RegistroWallet[]): RegistroWallet[] {
    return [...registros].sort((a, b) => Number(b.activa) - Number(a.activa));
  }

  formatearFecha(iso: string | null): string {
    if (!iso) return '—';
    const fecha = new Date(iso);
    if (isNaN(fecha.getTime())) return iso;
    return fecha.toLocaleString('es-CL', { timeZone: 'America/Santiago' });
  }

  private limpiarResultados(): void {
    this.resultadoWallet = null;
    this.resultadoUsuario = null;
  }

  private mostrarError(err: any): void {
    const mensaje =
      err?.error?.message ||
      err?.message ||
      'No se pudo completar la consulta.';
    Swal.fire('Error', Array.isArray(mensaje) ? mensaje.join(' ') : mensaje, 'error');
  }
}
